import React, { useEffect, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Checkbox, Divider, FormControlLabel, Paper, Stack, TextField, Typography,
} from '@mui/material';
import { SaveOutlined as SaveIcon } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import * as actions from '../../../../redux/actions';
import CustomerBankInfo from '../cards/CustomerBankInfo';

// แท็บ "ตั้งค่า" ของหน้าตั้งค่าคู่ยืม-คืน (คู่เดียว) — เก็บค่าที่คอลัมน์ของแถวคู่ยืม-คืนเอง:
//  - ชื่อรายการเจ้าหนี้ (loan_balance_label): ชื่อรายการที่แสดงยอดคงเหลือ (ตามจริง) ในการ์ดเจ้าหนี้
//    เว้นว่าง = ใช้ "ยืมเงิน"
//  - customer ของหน้า "บัญชีบริษัท" (company_customer_id): ติ๊ก = เป็นคนเดียวกับฝั่งยืม (null เหมือนเดิม)
//    ไม่ติ๊ก = เลือก customer อื่นเอง (ข้อมูลธนาคารมาจาก customer ที่เลือก) — ฝั่งยืมของคู่ไม่เปลี่ยน
export function LoanPairSettingsCard({
  pair, customerRows, findCustomer, customerName, onSaved,
}) {
  const dispatch = useDispatch();
  const [label, setLabel] = useState('');
  const [linked, setLinked] = useState(true);
  const [companyCustomerId, setCompanyCustomerId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  // โหลดค่าที่บันทึกไว้ (และโหลดใหม่เมื่อ reload หลังบันทึก)
  useEffect(() => {
    setLabel(pair?.loan_balance_label || '');
    setLinked(!pair?.company_customer_id);
    setCompanyCustomerId(pair?.company_customer_id || '');
  }, [pair?.id, pair?.loan_balance_label, pair?.company_customer_id]);

  const canSave = linked || Boolean(companyCustomerId);

  const submit = async () => {
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      await dispatch(actions.updateBankAccount({
        id: pair.id,
        loanBalanceLabel: label.trim() || null,
        companyCustomerId: linked ? null : companyCustomerId,
      }));
      setSaved(true);
      if (onSaved) onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!pair) {
    return (
      <Alert severity="info" sx={{ borderRadius: 2.5 }}>
        ต้องสร้างคู่ยืม-คืนก่อน (แท็บ &quot;คู่ยืม-คืน&quot;) จึงจะตั้งค่าส่วนนี้ได้
      </Alert>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 3, borderColor: '#dbeafe', bgcolor: '#fff' }}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {saved && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaved(false)}>บันทึกการตั้งค่าแล้ว</Alert>}

      <Typography variant="h6" sx={{ fontWeight: 750 }}>ชื่อรายการเจ้าหนี้</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        ชื่อรายการในการ์ดเจ้าหนี้ ที่แสดงยอดคงเหลือ (ตามจริง) ของคู่ยืม-คืน แสดงทุกครั้งที่ยอดไม่เท่ากับ 0
      </Typography>
      <TextField
        fullWidth
        label="ชื่อรายการ"
        placeholder="ยืมเงิน"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        helperText='เว้นว่าง = ใช้ข้อความ "ยืมเงิน"'
        inputProps={{ maxLength: 120 }}
      />

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" sx={{ fontWeight: 750 }}>Customer ของหน้าบัญชีบริษัท</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        หน้า &quot;บัญชีบริษัท&quot; (company-account) ใช้ Customer นี้ดึงรายรับ-รายจ่าย ฝั่งยืมของคู่ยืม-คืนไม่เปลี่ยนตามที่เลือกที่นี่
      </Typography>
      <FormControlLabel
        control={<Checkbox checked={linked} onChange={(e) => setLinked(e.target.checked)} />}
        label={`ใช้ Customer เดียวกับฝั่งยืม (${customerName(pair.customer_id)})`}
      />

      {!linked && (
        <Box sx={{ mt: 1.5 }}>
          <Autocomplete
            options={customerRows}
            getOptionLabel={(c) => c.name || ''}
            isOptionEqualToValue={(a, b) => a._id === b._id}
            value={findCustomer(companyCustomerId) || null}
            onChange={(e, newValue) => setCompanyCustomerId(newValue?._id || '')}
            renderInput={(params) => (
              <TextField
                {...params}
                label="เลือก Customer ของบัญชีบริษัท"
                helperText="ข้อมูลธนาคารมาจากข้อมูล Customer ที่เลือก"
              />
            )}
          />
          <CustomerBankInfo customer={findCustomer(companyCustomerId)} labelPrefix="บัญชีบริษัท — " sx={{ mt: 1.5 }} />
          <Alert severity="warning" sx={{ mt: 1.5, borderRadius: 2 }}>
            ยอดตั้งต้นและประวัติการปรับยอดของหน้าบัญชีบริษัทผูกกับแถวนี้ ถ้าเปลี่ยน Customer
            ควรกำหนดยอดตั้งต้นใหม่ในหน้าบัญชีบริษัทด้วย
          </Alert>
        </Box>
      )}

      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={submit} disabled={!canSave || saving}>
          {saving ? 'กำลังบันทึก...' : 'บันทึก'}
        </Button>
      </Stack>
    </Paper>
  );
}

export default LoanPairSettingsCard;
